// import { FC } from "react";
// import { useParams } from "react-router-dom";
// import {
//   FaUserCircle,
//   FaClock,
//   FaMapMarkerAlt,
//   FaUser,
//   FaSignInAlt,
//   FaEnvelope,
// } from "react-icons/fa";
// import { useUsers } from "../../api/api-hooks";

// const UserDetailCard: FC = () => {
//   const { data: usersData } = useUsers();

//   const { userId } = useParams();
//   const user: any = usersData?.items?.find(
//     (u: any) => u.user_id?.toLowerCase() === userId?.toLowerCase()
//   );

//   const userRole = user?.user_role.map((role: string) => role).join(", ");

//   if (!user) {
//     return (
//       <div className="max-w-2xl mx-auto p-6 text-center text-gray-600">
//         <h2 className="text-2xl font-bold">User Not Found</h2>
//         <p>The requested user does not exist.</p>
//       </div>
//     );
//   }

//   const DEFAULT_AVATAR_SVG = `
//   <svg
//     viewBox="0 0 24 24"
//     fill="none"
//     xmlns="http://www.w3.org/2000/svg"
//   >
//     <circle cx="12" cy="8" r="4" stroke="gray" strokeWidth="1.5" fill="white" />
//     <path
//       d="M4 20c0-4 4-6 8-6s8 2 8 6"
//       stroke="gray"
//       strokeWidth="1.5"
//       strokeLinecap="round"
//     />
//   </svg>
// `;

//   const DEFAULT_AVATAR = `data:image/svg+xml;base64,${btoa(
//     DEFAULT_AVATAR_SVG
//   )}`;

//   return (
//     <div className="max-w-3xl mx-auto px-6 py-8 bg-white shadow-lg rounded-xl border border-gray-200">
//       {/* Avatar */}
//       <h2 className="text-3xl font-bold mb-6 text-gray-800">User Details</h2>
//       <div className="flex flex-col items-center">
//         {DEFAULT_AVATAR ? (
//           <img
//             src={DEFAULT_AVATAR}
//             alt={user.name}
//             className="w-24 h-24 rounded-full border-4 border-gray-300"
//           />
//         ) : (
//           <FaUserCircle className="w-24 h-24 text-gray-400" />
//         )}

//         <h3 className="text-2xl font-semibold mt-4">{user.name}</h3>
//         <p className="text-gray-500 flex items-center gap-2">
//           <FaEnvelope className="text-blue-500" />
//           {user?.user_email}
//         </p>
//         <span className="px-4 py-1 text-sm mt-2 bg-blue-100 text-blue-700 rounded-full flex items-center gap-2">
//           <FaUser /> {userRole}
//         </span>
//       </div>

//       {/* User Details */}
//       <div className="mt-6 text-left px-6">
//         <p className="flex items-center gap-3 text-gray-700 text-lg">
//           <FaUser className="text-blue-500" />
//           <span className="font-semibold">User ID:</span> {user.id}
//         </p>
//         <p className="flex items-center gap-3 text-gray-700 text-lg">
//           <FaClock className="text-green-500" />
//           <span className="font-semibold">Session Duration:</span>{" "}
//           {user.sessionDuration}
//         </p>
//         <p className="flex items-center gap-3 text-gray-700 text-lg">
//           <FaMapMarkerAlt className="text-red-500" />
//           <span className="font-semibold">IP Address:</span> {user.ipAddress}
//         </p>
//         <p
//           className={`flex items-center gap-3 mt-4 text-lg font-semibold ${
//             user.loggedIn ? "text-green-600" : "text-red-500"
//           }`}
//         >
//           <FaSignInAlt />
//           {user.loggedIn ? "Active Session" : "Logged Out"}
//         </p>
//       </div>
//     </div>
//   );
// };

// export default UserDetailCard;

import React, { useMemo, useState } from "react";
import type { FC, ReactNode } from "react";
import useCommonStore from "../../store/CommonStore";
import { getDateRangeFromSelectedValue } from "../../common/Constants";
import { usePagination } from "../hooks/usePagination";
import {
  useChatsPage,
  useFeedbackPage,
  useProjectsPage,
  useSessionsPage,
  useUserDetailsById,
} from "../../api/api-hooks";
import TabbedTanstackTable from "../../common/TabbedTanstackTable";
import { useParams } from "react-router-dom";
import { formatDuration } from "../../utils/date-utils";

type StatProps = {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
};

type TabType = "chats" | "projects" | "agents" | "feedbacks" | "sessions";

const Stat: FC<StatProps> = ({ label, value, sub }) => (
  <div className="rounded-lg border bg-white p-4 shadow-sm">
    <div className="space-y-2">
      <p className="text-sm text-gray-600">{label}</p>
      <p className="text-2xl font-semibold text-gray-900">{value}</p>
      {sub && <p className="text-sm text-gray-500">{sub}</p>}
    </div>
  </div>
);

const TabbedContent: FC = () => {
  const { userId } = useParams();
  const [activeTab, setActiveTab] = useState<TabType>("chats");

  const { selectedValue, selectedFeedback, setPageIndex } = useCommonStore();
  const dateRange = getDateRangeFromSelectedValue(selectedValue);

  const { pageIndex, pageSize } = usePagination();

  const isAllData = dateRange?.isAllData ?? false;
  const startDate = isAllData ? undefined : dateRange?.startDate ?? "";
  const endDate = isAllData ? undefined : dateRange?.endDate ?? "";

  const isChatsActive = activeTab === "chats";
  const isProjectsActive = activeTab === "projects";
  const isAgentsActive = activeTab === "agents";
  const isSessionsActive = activeTab === "sessions";
  const isFeedbacksActive = activeTab === "feedbacks";

  const { data: chats, isLoading: isChatsLoading } = useChatsPage(
    pageIndex + 1,
    pageSize,
    startDate,
    endDate,
    "rolesgpt",
    "",
    isAllData,
    "",
    userId,
    isChatsActive
  );
  const { data: projects, isLoading: isProjectsLoading } = useProjectsPage(
    pageIndex + 1,
    pageSize,
    startDate,
    endDate,
    isAllData,
    userId,
    isProjectsActive
  );

  const { data: feedbacks, isLoading: isFeedbackLoading } = useFeedbackPage(
    pageIndex + 1,
    pageSize,
    startDate,
    endDate,
    isAllData,
    selectedFeedback?.value,
    userId,
    isFeedbacksActive
  );

  const {
    data: sessions,
    isLoading: isSessionsLoading,
    // error: chatsError,
  } = useSessionsPage(
    pageIndex + 1,
    pageSize,
    startDate,
    endDate,
    isAllData,
    userId,
    isSessionsActive
  );

  const { data: agentAppChats, isLoading: isAgentAppChatsLoading } =
    useChatsPage(
      pageIndex + 1,
      pageSize,
      startDate,
      endDate,
      "HR App",
      "",
      isAllData,
      "",
      userId,
      isAgentsActive
    );

  const tabs = [
    { id: "chats" as const, label: "Chats", count: chats?.total ?? 0 },
    {
      id: "projects" as const,
      label: "Projects",
      count: projects?.total ?? 0,
    },
    {
      id: "agents" as const,
      label: "Agents",
      count: agentAppChats?.total ?? 0,
    },
    {
      id: "sessions" as const,
      label: "Sessions",
      count: sessions?.total ?? 0,
    },
    {
      id: "feedbacks" as const,
      label: "Feedbacks",
      count: feedbacks?.total ?? 0,
    },
  ];

  // ✅ choose props by active tab
  const tableProps =
    activeTab === "chats"
      ? {
          kind: "chats" as const,
          rows: chats?.items ?? [],
          data: chats ?? {},
          title: "Recent Conversations",
          loading: isChatsLoading,
        }
      : activeTab === "projects"
      ? {
          kind: "projects" as const,
          rows: projects?.items ?? [],
          data: projects ?? {},
          title: "Active Projects",
          loading: isProjectsLoading,
        }
      : activeTab === "agents"
      ? {
          kind: "agents" as const,
          rows: agentAppChats?.items ?? [],
          title: "Agent Usage",
          loading: isAgentAppChatsLoading,
          data: agentAppChats ?? {},
        }
      : activeTab === "sessions"
      ? {
          kind: "sessions" as const,
          rows: sessions?.Sessions ?? [],
          data: sessions ?? {},
          title: "User Sessions",
          loading: isSessionsLoading,
        }
      : {
          kind: "feedbacks" as const,
          rows: feedbacks?.items ?? [],
          data: feedbacks ?? {},
          title: "User Feedback",
          loading: isFeedbackLoading,
        };

  return (
    <div className="rounded-lg border bg-white shadow-sm overflow-hidden">
      <div className="border-b">
        <nav className="flex bg-gray-50">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setPageIndex?.(0);
              }}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "bg-white text-gray-900 border-gray-900"
                  : "text-gray-600 hover:text-gray-900 border-transparent hover:bg-gray-100"
              }`}
            >
              <div className="flex items-center gap-3">
                <span>{tab.label}</span>
                <span className="bg-gray-200 text-gray-700 rounded-full px-2.5 py-0.5 text-xs font-medium">
                  {tab.count}
                </span>
              </div>
            </button>
          ))}
        </nav>
      </div>

      <div className="p-0">
        <TabbedTanstackTable {...tableProps} />
      </div>
    </div>
  );
};

// Accept both shapes from the API
type AgentsUsageObj = Record<string, { count: number; percentage?: number }>;
type AgentsUsageNum = Record<string, number>;
type AgentsUsageAny = AgentsUsageObj | AgentsUsageNum;

const AgentUsageChart: FC<{ agents_usage?: AgentsUsageAny }> = ({
  agents_usage,
}) => {
  // Normalize to [agent, {count, percentage?}][]
  const rows = useMemo(() => {
    const entries = Object.entries(agents_usage ?? {});
    return entries
      .map<[string, { count: number; percentage?: number }]>(([agent, v]) =>
        typeof v === "number" ? [agent, { count: v }] : [agent, v]
      )
      .sort((a, b) => (b[1].count ?? 0) - (a[1].count ?? 0));
  }, [agents_usage]);

  const total = rows.reduce((sum, [, v]) => sum + (v.count ?? 0), 0) || 1;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-medium text-gray-900">HR Agent Usage</h3>
        <span className="text-sm text-gray-500">{total} total uses</span>
      </div>

      <div className="space-y-4">
        {rows.map(([agent, { count, percentage }]) => {
          // Use API percentage if provided; otherwise compute it
          const pct = percentage ?? Math.round(((count ?? 0) / total) * 100);

          return (
            <div key={agent} className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">{agent}</span>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>{count ?? 0} uses</span>
                  <span className="text-gray-400">•</span>
                  <span>{pct}%</span>
                </div>
              </div>
              <div className="h-2 w-full rounded bg-gray-200 overflow-hidden">
                <div
                  className="h-full bg-gray-600 transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const UserDetailCard: FC = () => {
  // const recentSessions = DATA.sessions.slice(0, 6);
  const { userId } = useParams();
  const { data: userData } = useUserDetailsById(userId || "");

  const user = userData?.user;
  const userStats = userData?.stats;

  type AgentsUsage =
    | Record<string, { count: number; percentage?: number }>
    | Record<string, number>
    | undefined;

  function computeAgentAggregates(agents_usage: AgentsUsage) {
    const entries = Object.entries(agents_usage ?? {});

    const getCount = (v: unknown) =>
      typeof v === "number" ? v : (v as { count?: number })?.count ?? 0;

    const usedEntries = entries.filter(([, v]) => getCount(v) > 0);

    const total_agents = usedEntries.length;

    const distinctAgentsUsed = total_agents;

    const topAgent = usedEntries[0]?.[0] ?? "—";

    return { total_agents, distinctAgentsUsed, topAgent };
  }

  const { total_agents } = useMemo(
    () => computeAgentAggregates(userStats?.agents_usage as AgentsUsage),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userStats]
  );

  const statConfigs = [
    {
      label: "Total RolesGpt Chats",
      value: userStats?.total_chats_rolesgpt,
      // sub: `${stats.totalPrompts} prompts`,
    },
    {
      label: "Total HR Chats",
      value: userStats?.total_chats_hr,
      // sub: `${stats.totalPrompts} prompts`,
    },
    // {
    //   label: "Avg Chat Time",
    //   value: fmtDur(stats.avgChatTime),
    //   sub: "per conversation",
    // },
    {
      label: "Active Projects",
      value: userStats?.total_boards,
      // sub: "ongoing",
    },
    // {
    //   label: "Feedback Score",
    //   value: `${Math.round(
    //     (DATA.feedbacks.filter((f) => f.type === "Thumbs Up").length /
    //       DATA.feedbacks.length) *
    //       100
    //   )}%`,
    //   sub: `${userStats?.total_feedback} total`,
    // },
    {
      label: "Total Feedback",
      value: userStats?.total_feedback,
      // sub: `${userStats?.total_feedback} total`,
    },
    {
      label: "Total Sessions",
      value: userStats?.total_sessions,
    },
    {
      label: "Session Time",
      value: formatDuration(userStats?.average_session_time),
      // sub: "average",
    },
    {
      label: "Used AI Agents",
      value: total_agents,
      // sub: `Top: ${stats.topAgent}`,
    },
    // {
    //   label: "Last Active",
    //   value: fmtDT(user?.lastActive),
    //   sub: "recent",
    // },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl p-6 space-y-8">
        {/* Header */}
        <div className="rounded-lg bg-white border p-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <h1 className="text-3xl font-bold text-gray-900">
                {user?.username}
              </h1>
              <div className="space-y-1 text-gray-600">
                <p>{user?.email}</p>
                <p className="capitalize">{user?.role.join(", ")}</p>
              </div>
            </div>

            <div className="flex gap-3">
              {/* <button className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                Impersonate
              </button> */}
              <button className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800">
                Export Data
              </button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statConfigs.map((stat, index) => (
            <Stat key={index} {...stat} />
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - 2/3 width */}
          <div className="lg:col-span-2 space-y-6">
            <TabbedContent />
          </div>

          {/* Right Column - 1/3 width */}
          <div className="space-y-6">
            {/* Agent Usage */}
            <div className="rounded-lg bg-white border shadow-sm overflow-hidden">
              <AgentUsageChart agents_usage={userStats?.agents_usage} />
            </div>

            {/* Quick Actions */}
            <div className="rounded-lg bg-white border p-6 shadow-sm">
              <h3 className="font-medium text-gray-900 mb-4">Quick Actions</h3>

              <div className="space-y-3">
                <button className="w-full text-left rounded-lg border p-3 hover:bg-gray-50">
                  <span className="text-sm font-medium text-gray-900">
                    Add Note
                  </span>
                </button>
                <button className="w-full text-left rounded-lg border p-3 hover:bg-gray-50">
                  <span className="text-sm font-medium text-gray-900">
                    Schedule Call
                  </span>
                </button>
                <button className="w-full text-left rounded-lg border p-3 hover:bg-gray-50">
                  <span className="text-sm font-medium text-gray-900">
                    Edit Profile
                  </span>
                </button>
              </div>
            </div>

            {/* Account Notes */}
            <div className="rounded-lg bg-white border p-6 shadow-sm">
              <h3 className="font-medium text-gray-900 mb-4">Account Notes</h3>

              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex gap-3 items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                  <p>
                    Heavy user of Candidate Screening & Interview Prep agents
                  </p>
                </div>
                <div className="flex gap-3 items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                  <p>Interested in organization-wide onboarding automation</p>
                </div>
                <div className="flex gap-3 items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                  <p>
                    Potential enterprise pilot: 10 seats, quarterly review
                    scheduled
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="rounded-lg bg-white border p-4 shadow-sm">
          <p className="text-center text-xs text-gray-500">
            Live Dashboard • Data refreshes automatically • Mock data generated
            for demonstration
          </p>
        </div>
      </div>
    </div>
  );
};

export default UserDetailCard;
